import { Avatar, Button, Dropdown, Grid, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { getLoginRedirectUrl, getLogoutRedirectUrl } from "../routes/apiRoutes";
import { appStore } from "../store/store";
import { getAvatarUrl } from "../utils/misc";

import style from "./UserBlock.module.css";
import { NextgisServicesMenu } from "./services-menu";

import {
  CaretDownOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";

import "./userBlock.css";

const { useBreakpoint } = Grid;

const LoginBtn = observer(() => {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <a href={getLoginRedirectUrl(location.pathname)} target="_self">
      <Button type="link">
        <span className={style.loginButtonText}>{t("navigation.login")}</span>
      </Button>
    </a>
  );
});

LoginBtn.displayName = "LoginBtn";

const Profile = observer(() => {
  const { t } = useTranslation();

  const [gravatarUrl, setGravatarUrl] = useState<string | undefined>();

  const { user } = appStore;

  useEffect(() => {
    const fetchAvatar = async () => {
      if (user?.email) {
        const url = await getAvatarUrl(user.email);
        if (url) {
          setGravatarUrl(url);
        }
      }
    };
    fetchAvatar();
  }, [user]);

  const profileMenuItem = {
    label: (
      <a
        className={style["a-profile-menu-item"]}
        href={`https://my.nextgis.com/profile`}
        target="_self"
        rel="noopener noreferrer"
      >
        <UserOutlined style={{ fontSize: 16 }} /> &nbsp;&nbsp;{" "}
        {t("navigation.profile")}
      </a>
    ),
    key: "profile",
  };

  const logoutMenuItem = {
    label: (
      <a
        className={style["a-profile-menu-item"]}
        href={getLogoutRedirectUrl()}
        target="_self"
        rel="noopener noreferrer"
      >
        <LogoutOutlined style={{ fontSize: 16 }} /> &nbsp;&nbsp;{" "}
        {t("navigation.logout")}
      </a>
    ),
    key: "sign_out",
  };

  const profileDropDownItems: MenuProps["items"] = [
    profileMenuItem,
    logoutMenuItem,
  ];

  const screens = useBreakpoint();
  return (
    <>
      <Dropdown
        menu={{ items: profileDropDownItems }}
        arrow={true}
        trigger={["click"]}
      >
        <a className={style["a-profile"]} onClick={(e) => e.preventDefault()}>
          <Avatar
            style={{ marginBottom: "3px" }}
            icon={
              gravatarUrl ? (
                <img src={gravatarUrl} alt="avatar" />
              ) : (
                <UserOutlined />
              )
            }
          />
          {user?.username && screens.md && (
            <>
              &nbsp;&nbsp;{user.username} <CaretDownOutlined />
            </>
          )}
        </a>
      </Dropdown>
    </>
  );
});

Profile.displayName = "Profile";

export const UserBlock = observer(() => {
  const isAdmin = appStore.user?.is_staff;
  const loggedIn = Boolean(appStore.loggedIn);
  const { t } = useTranslation();

  return (
    <div className={style.userBlockWrapper}>
      {loggedIn ? (
        <div className={style.userBlockFlexInner}>
          <div className={style.userBlockFlexInner}>
            {isAdmin && (
              <div>
                <a href={"/admin/"} target="_blanc">
                  <Tooltip title={t("navigation.admin")} mouseEnterDelay={0.3}>
                    <Button
                      type="primary"
                      icon={<SettingOutlined style={{ fontSize: 16 }} />}
                    />
                  </Tooltip>
                </a>
              </div>
            )}
          </div>
          <NextgisServicesMenu isLoggedIn={loggedIn} />
          <Profile />
        </div>
      ) : (
        <>
          <NextgisServicesMenu isLoggedIn={loggedIn} />
          <LoginBtn />
        </>
      )}
    </div>
  );
});

UserBlock.displayName = "UserBlock";
